/**
 * A chave de cidade: a única régua de "esta cidade é aquela?" do produto.
 *
 * ─── Por que existe (tarefa wdwxndz2y9) ─────────────────────────────────────
 *
 * Até 06/10/2026 havia duas réguas. O Dashboard, o mapa da área e o filtro de
 * cidade usavam `semUF`; o selo "Fora da área" do Funil, o aviso do Dossiê, a
 * conferência do CNPJ e a triagem do servidor usavam `normalizeCityName`, que
 * cortava o estado ANTES de tirar o espaço do fim. Medido no ensaio em 17/09: o
 * lead "São José dos Campos SP " (com espaço no fim) da Jacareí aparecia dentro
 * da área no Dashboard e com o selo de fora no Funil. A mesma pessoa, dois
 * veredictos, em duas telas.
 *
 * Agora as duas réguas são esta. `semUF` (`lib/leadFilters.ts`) e
 * `normalizeCityName` (`lib/utils.ts`) devolvem `chaveDaCidade`, e o servidor
 * (`supabase/functions/_shared/area-triage.ts → normalizeCity`) é uma cópia
 * exata, conferida por teste (`tests/area/chave-da-cidade.test.ts`). A Edge
 * Function não importa de `src/`, por isso a cópia.
 *
 * Medido na produção em 06/10/2026, antes de trocar: de 7.720 leads com cidade,
 * 1 mudou de lado no Funil (o da Jacareí, de fora para dentro, que é o que o
 * Dashboard já dizia); dos endereços de CNPJ, 0; na triagem do servidor, 0. Em
 * ~3.000 grafias distintas de leads, endereços, clientes do SOMA e áreas, 4
 * chaves mudaram, e todas para a certa: "São José dos Campos SP ", "São Pedro,
 * Sp" (a vírgula), "SANTA FE" e "BONITO DE SANTA FE" (a régua antiga cortava o
 * "FE" como se fosse estado).
 *
 * ─── 🚨 SÃO QUATRO FORMATOS, NÃO TRÊS ───────────────────────────────────────
 *
 * Contei na produção antes de escrever o corte — 529 cidades distintas em 4.206
 * leads (26/08/2026):
 *
 *   "Jundiaí/SP"     2227 leads   (formulário)
 *   "Jundiaí"        1975 leads   (digitado à mão)
 *   "Jundiaí-SP"        4 leads   (SOMA)
 *   "Serra ES"         38 valores (espaço, e às vezes vírgula: "São Pedro, Sp")
 *
 * O corte usa a LISTA das 27 UFs, e não "duas letras no fim". Com "duas letras"
 * corta o fim de qualquer cidade cuja última palavra tenha duas letras: foi o
 * que a régua antiga fez com "Santa Fe". Lista fechada não tem esse dia.
 *
 * E só corta se sobrar alguma coisa: há leads cuja cidade inteira é "ES", "SP"
 * e "Rj". Cortar ali deixaria a string vazia, e o vazio casa com tudo.
 *
 * ⚠️ Antes do corte vêm, nesta ordem: sem acento, minúsculas, espaços das pontas
 * fora e espaços repetidos juntados ("Vila Velha  "). Cortar antes de aparar
 * era o defeito de 17/09: o espaço do fim escondia o estado do corte.
 *
 * ─── O estado por extenso (tarefa wdwxne05ez) ───────────────────────────────
 *
 * Em 23/10/2026 um lead da Sorocaba respondeu "Itu - São Paulo". A chave ficava
 * "itu - sao paulo", não casava com "Itu/SP", e o robô disse a quem mora em
 * cidade atendida que ali não se atendia. Medido na produção (30 dias): 1 caso
 * em 64 reprovações por fora da área.
 *
 * Depois da sigla, sai também o nome do estado por extenso, mas SÓ quando vem
 * depois de separador explícito e sobra nome de cidade:
 *
 *   vírgula ou barra, com ou sem espaço:  "Itu, São Paulo", "Itu/Sao Paulo"
 *   hífen com espaço em pelo menos um lado: "Itu - São Paulo", "Itu -São Paulo"
 *
 * Só espaço não corta: "Alto Paraná", "Mato Grosso" (cidade da PB) e
 * "Espírito Santo do Pinhal" ficam inteiras. Hífen colado não corta:
 * "Ji-Paraná" é cidade de Rondônia, não Ji no Paraná. E o nome do estado
 * sozinho ("São Paulo", "Rio de Janeiro") é a capital, porque não sobra nada
 * antes dele.
 *
 * ─── O nome curto (tarefa wdwxne035x) ───────────────────────────────────────
 *
 * A chave não sabe que "Bragança" é "Bragança Paulista". Quem pergunta "esta
 * cidade está na área?" usa `casaComCidadeDaArea`, que aceita também o nome
 * curto inequívoco do mesmo estado (tabela e regra em `apelidosDeCidade.ts`).
 * A chave em si não muda: o agrupamento do Dashboard e a busca seguem iguais.
 */
import { APELIDOS_DE_CIDADE } from "@/lib/apelidosDeCidade"

/** As 27 unidades da federação, em minúsculas. */
export const UFS = [
  "ac", "al", "ap", "am", "ba", "ce", "df", "es", "go", "ma", "mt", "ms", "mg",
  "pa", "pb", "pr", "pe", "pi", "rj", "rn", "rs", "ro", "rr", "sc", "sp", "se", "to",
] as const

/** Sem acento, minúsculas, sem espaço nas pontas e sem espaço repetido. */
function limpar(raw: string | null | undefined): string {
  if (!raw) return ""
  return String(raw)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
}

/** Os 27 estados por extenso, sem acento e em minúsculas, com a sigla de cada um. */
export const ESTADOS_POR_EXTENSO: Readonly<Record<string, (typeof UFS)[number]>> = {
  "acre": "ac", "alagoas": "al", "amapa": "ap", "amazonas": "am", "bahia": "ba", "ceara": "ce",
  "distrito federal": "df", "espirito santo": "es", "goias": "go", "maranhao": "ma", "mato grosso": "mt",
  "mato grosso do sul": "ms", "minas gerais": "mg", "para": "pa", "paraiba": "pb", "parana": "pr",
  "pernambuco": "pe", "piaui": "pi", "rio de janeiro": "rj", "rio grande do norte": "rn",
  "rio grande do sul": "rs", "rondonia": "ro", "roraima": "rr", "santa catarina": "sc", "sao paulo": "sp", // acentos-ok: chave de comparação, sem acento de propósito
  "sergipe": "se", "tocantins": "to",
}

/**
 * Separador explícito antes do estado por extenso: vírgula ou barra (com ou sem
 * espaço), ou hífen com espaço em pelo menos um lado. Só espaço e hífen colado
 * não contam ("Alto Paraná", "Ji-Paraná").
 */
const ESTADO_POR_EXTENSO_NO_FIM = new RegExp(
  `^(.*?)(?:\\s*[,/]\\s*|\\s+-\\s*|\\s*-\\s+)(${Object.keys(ESTADOS_POR_EXTENSO).join("|")})$`,
)

/** A cidade sem o estado do fim, e o estado, quando há um. */
export function separarUF(raw: string | null | undefined): { cidade: string; uf: string | null } {
  let cidade = limpar(raw)
  let uf: string | null = null
  const m = cidade.match(/^(.*?)[\s,/-]+([a-z]{2})$/)
  if (m && m[1].trim() && (UFS as readonly string[]).includes(m[2])) {
    cidade = m[1].trim()
    uf = m[2]
  }
  // Depois da sigla, o estado por extenso: "Itu - São Paulo" e "Itu - São Paulo - SP".
  const e = cidade.match(ESTADO_POR_EXTENSO_NO_FIM)
  if (e && e[1].trim()) {
    cidade = e[1].trim()
    uf = uf ?? ESTADOS_POR_EXTENSO[e[2]]
  }
  return { cidade, uf }
}

/**
 * A chave de comparação: "Jundiaí/SP", "Jundiaí", "JUNDIAI", "Jundiaí-SP",
 * "jundiai sp ", "Jundiaí, SP" e "Jundiaí - São Paulo" dão todas "jundiai".
 */
export function chaveDaCidade(raw: string | null | undefined): string {
  return separarUF(raw).cidade
}

/** As duas grafias são a mesma cidade? Vazio nunca é igual a nada. */
export function mesmaCidade(a: string | null | undefined, b: string | null | undefined): boolean {
  const ka = chaveDaCidade(a)
  return ka !== "" && ka === chaveDaCidade(b)
}

/**
 * `curta` é o nome curto de `inteira`, no mesmo estado? Estado diferente nunca
 * casa; sem estado dos dois lados, vale o de qualquer estado em que o nome
 * curto aponte para aquele nome inteiro.
 */
function ehApelidoDe(curta: { cidade: string; uf: string | null }, inteira: { cidade: string; uf: string | null }): boolean {
  if (curta.uf && inteira.uf && curta.uf !== inteira.uf) return false
  const uf = curta.uf ?? inteira.uf
  const ufs = uf ? [uf] : Object.keys(APELIDOS_DE_CIDADE)
  return ufs.some((u) => {
    const complemento = APELIDOS_DE_CIDADE[u]?.[curta.cidade]
    return complemento !== undefined && `${curta.cidade} ${complemento}` === inteira.cidade
  })
}

/**
 * A cidade informada é esta cidade da área? A chave igual responde sim; senão,
 * o nome curto inequívoco no mesmo estado também ("Bragança" e "Bragança
 * Paulista/SP"; "Santa Bárbara" e "Santa Bárbara d'Oeste/SP"). "Bragança/PA"
 * não casa com "Bragança Paulista/SP", e "Mogi" não casa com nada (tarefa
 * wdwxne035x). Vazio nunca casa.
 */
export function casaComCidadeDaArea(cidadeDaArea: string | null | undefined, cidadeInformada: string | null | undefined): boolean {
  if (mesmaCidade(cidadeDaArea, cidadeInformada)) return true
  const area = separarUF(cidadeDaArea)
  const informada = separarUF(cidadeInformada)
  if (!area.cidade || !informada.cidade) return false
  return ehApelidoDe(informada, area) || ehApelidoDe(area, informada)
}
